package com.sddamico.draftmachine.components.pwa

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.api.apiGet
import com.sddamico.draftmachine.api.apiJson
import com.sddamico.draftmachine.api.apiPost
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.displayInlineFlex
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.glowGlass
import com.sddamico.draftmachine.ds.core.Card
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonSize
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.models.PushSubscribeRequest
import com.sddamico.draftmachine.models.SuccessResponse
import com.sddamico.draftmachine.models.VapidKeyResponse
import com.sddamico.draftmachine.util.BrowserPushSubscription
import com.sddamico.draftmachine.util.currentPushSubscription
import com.sddamico.draftmachine.util.ensureServiceWorker
import com.sddamico.draftmachine.util.isPushSupported
import com.sddamico.draftmachine.util.PushPermissionDeniedException
import com.sddamico.draftmachine.util.notificationPermission
import com.sddamico.draftmachine.util.subscribeToPush
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.localStorage
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import kotlin.js.Date

private const val SNOOZE_KEY = "draft-machine:push-snoozed-until"
private const val SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000L

@Composable
fun PushNotificationBanner() {
  val scope = rememberCoroutineScope()
  var visible by remember { mutableStateOf(false) }
  var enabling by remember { mutableStateOf(false) }
  var error by remember { mutableStateOf<String?>(null) }
  // Preloaded so the Enable click has NO network await before requestPermission()/subscribe() —
  // Android Chrome drops the user activation across an intervening await and push silently fails.
  var vapidKey by remember { mutableStateOf<String?>(null) }

  LaunchedEffect(Unit) {
    if (!isPushSupported() || notificationPermission() == "denied") return@LaunchedEffect
    try {
      ensureServiceWorker()
      vapidKey = apiGet("/api/push/vapid-key", VapidKeyResponse.serializer()).publicKey
      val current = currentPushSubscription()
      if (current != null) {
        saveSubscription(current)
        return@LaunchedEffect
      }
      // Signing out un-enrolls the browser but leaves the permission granted, so there is nothing
      // to ask: re-enroll quietly, or alerts would stay off until someone found /settings/alerts.
      if (notificationPermission() == "granted") {
        vapidKey?.let { saveSubscription(subscribeToPush(it)) }
        return@LaunchedEffect
      }
      val snoozedUntil = localStorage.getItem(SNOOZE_KEY)?.toDoubleOrNull() ?: 0.0
      visible = notificationPermission() == "default" && Date.now() >= snoozedUntil
    } catch (_: Throwable) {
      visible = false
    }
  }

  if (!visible) return

  Card(modifier = Modifier.fillMaxWidth(), riveted = true, heat = true, padding = 16.px) {
    Div(
      attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(14.px)
        .styleModifier { property("text-align", "left") }.toAttrs()
    ) {
      Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Start).gap(12.px).toAttrs()) {
        BellIndicator()
        Div(attrs = Modifier.flex(1).minWidth(0.px).displayFlex()
          .flexDirection(FlexDirection.Column).gap(3.px).toAttrs()) {
          SpanText(
            "On-clock alerts",
            Modifier.dmStamp().color(DmColor.glass400).displayBlock(),
          )
          SpanText(
            "Your turn, wherever you are",
            Modifier.fontFamily(*DmType.body).fontSize(DmType.textBase).fontWeight(DmType.weightBold)
              .lineHeight(1.2).color(DmColor.text).displayBlock(),
          )
          SpanText(
            error ?: "Enable browser alerts and the machine will ring the moment you're on the clock.",
            Modifier.fontFamily(*DmType.body).fontSize(DmType.textXs).lineHeight(1.5)
              .color(if (error == null) DmColor.textMuted else DmColor.rust400).displayBlock().margin(top = 1.px),
          )
        }
      }
      Div(
        attrs = Modifier.displayFlex().alignItems(AlignItems.Center)
          .justifyContent(JustifyContent.FlexEnd).gap(8.px).toAttrs()
      ) {
        Button(
          "Not now", variant = ButtonVariant.Ghost, size = ButtonSize.Sm,
          onClick = {
            localStorage.setItem(SNOOZE_KEY, (Date.now() + SEVEN_DAYS_MS).toString())
            visible = false
          },
        )
        Button(
          "Enable alerts", variant = ButtonVariant.Primary, size = ButtonSize.Sm,
          icon = "bell-ringing", loading = enabling,
          onClick = {
            if (!enabling) scope.launch {
              enabling = true
              error = null
              try {
                // Use the preloaded key so there is no await before subscribeToPush; only fetch as a
                // fallback if preload failed (that fetch will consume the gesture, but it's better than nothing).
                val key = vapidKey ?: apiGet("/api/push/vapid-key", VapidKeyResponse.serializer()).publicKey
                val subscription = subscribeToPush(key)
                saveSubscription(subscription)
                localStorage.removeItem(SNOOZE_KEY)
                visible = false
              } catch (_: PushPermissionDeniedException) {
                // Browser blocked notifications — no point re-prompting; drop the banner.
                visible = false
              } catch (e: Throwable) {
                error = "Couldn't turn on alerts: ${e.message ?: "unknown error"}"
              }
              enabling = false
            }
          },
        )
      }
    }
  }
}

/** A lit "energized glass" socket housing the alert bell — the on-clock indicator motif. */
@Composable
private fun BellIndicator() {
  Div(
    attrs = Modifier
      .displayInlineFlex().alignItems(AlignItems.Center).justifyContent(JustifyContent.Center)
      .size(40.px).flex("0 0 auto")
      .backgroundColor(DmColor.glass700)
      .border(1.px, LineStyle.Solid, DmColor.glass600)
      .borderRadius(DmEffects.radiusLg)
      .glowGlass()
      .toAttrs { attr("aria-hidden", "true") }
  ) {
    Icon("bell-ringing", IconWeight.Fill, Modifier.fontSize(20.px).color(DmColor.glass300))
  }
}

private suspend fun saveSubscription(subscription: BrowserPushSubscription) {
  val body = apiJson.encodeToString(
    PushSubscribeRequest(subscription.endpoint, subscription.p256dh, subscription.auth),
  )
  apiPost("/api/push/subscribe", body, SuccessResponse.serializer())
}
